import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Spinner } from 'react-bootstrap';
import Card from '../Card';
import Select from '../../../components/Select';
import DatePicker from '../../../components/Form/DatePicker';
import useGetFarms from '../../../hooks/useGetFarms';
import ModalComponent from '../../../components/ModalComponent';
import excel from '../../../services/excel';

function ExcelDownload() {
  const [fromDate, setFromDate] = useState(dayjs().format('YYYY/MM'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY/MM'));
  const [toMonthKey, setToMonthKey] = useState(0);
  const [fromMonthKey] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState('');
  const [showDownloadAllModal, setShowDownloadAllModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const {
    farmId, farms, setFarmId,
  } = useGetFarms(true);

  const download = async (isAll = false) => {
    setBackendErrorMessage('');
    if (isAll) {
      setDownloadingAll(true);
    } else {
      setDownloading(true);
    }
    const form = isAll ? { all: true } : {
      fromDate: `${fromDate}/01`,
      toDate: `${toDate}/01`,
      farmId: farmId === '全農場' ? null : farmId,
    };

    try {
      const response = await excel.downloadFile(form);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const excelFileName = `${fromDate}_${toDate}_data_${dayjs().format('YYYY-MM-DD_HH_mm')}.xlsx`;

      link.href = url;
      link.download = excelFileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      if (e?.response?.data?.message) {
        setBackendErrorMessage(e.response.data.message);
      }
      setDownloadingAll(false);
      setDownloading(false);
    } finally {
      if (isAll) {
        setDownloadingAll(false);
      } else {
        setDownloading(false);
      }
    }
  };

  useEffect(() => {
    if (fromDate > toDate) {
      setToDate(`${fromDate}`);
    }
    setToMonthKey((prev) => prev + 1);
  }, [fromDate, toDate]);

  return (
    <main className="content p-3">
      <ModalComponent
        title="全Excelダウンロード確認"
        body="全データをダウンロードします。よろしいですか？"
        confirmButtonText="ダウンロードする"
        confirmButtonColor="primary"
        onConfirm={() => download(true)}
        show={showDownloadAllModal}
        closeModal={() => setShowDownloadAllModal(false)}
      />
      <ModalComponent
        title="Excelダウンロード確認"
        body="選択した情報でExcelをダウンロードします。よろしいですか？"
        confirmButtonText="ダウンロードする"
        confirmButtonColor="success"
        onConfirm={() => download()}
        show={showDownloadModal}
        closeModal={() => setShowDownloadModal(false)}
      />
      <Card createButtonText="新規保存" title="Excelダウンロード" noHeader>
        <div className="text-danger mb-3">
          { backendErrorMessage }
        </div>
        <div className="row row-cols-auto g-1 mt-n3 mb-n2">
          <div className="row row-cols-auto g-1" data-dashlane-rid="b5de92df6ab9bc27" data-form-type="other">
            <div className="col">
              <div className="row row-cols-auto g-1 me-1">
                <label className="col col-form-label">農場：</label>
                <div className="col">
                  <Select hasSelectMessage={farms.length === 0} value={farmId} options={farms?.map((farm) => ({ value: farm.farm_id, label: farm.farm_id }))} onChange={(param) => setFarmId(param)} />
                </div>
              </div>
            </div>
            <div className="col">
              <div className="row row-cols-auto g-1 me-2">
                <div className="col"><label className="form-control-plaintext">年月：</label></div>
                <div className="col">
                  <DatePicker
                    key={fromMonthKey}
                    initialDate={dayjs(fromDate).format('YYYY/MM') || dayjs().format('YYYY/MM')}
                    onChange={(e) => {
                      setFromDate(dayjs(e).format('YYYY/MM'));
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col">
              <div className="row row-cols-auto g-1 me-2">
                <div className="col"><label className="form-control-plaintext">～</label></div>
                <div className="col">
                  <DatePicker
                    key={fromMonthKey + toMonthKey}
                    minDate={dayjs(fromDate).format('YYYY/MM')}
                    id="toMonth"
                    initialDate={dayjs(toDate).format('YYYY/MM') || dayjs().format('YYYY/MM')}
                    onChange={(e) => {
                      setToDate(dayjs(e).format('YYYY/MM'));
                    }}
                  />
                </div>
              </div>
            </div>
            <div>
              <button
                disabled={downloading}
                onClick={() => setShowDownloadModal(true)}
                className="btn btn-success"
                type="button"
              >
                {
                    downloading
                    && <Spinner size="sm" animation="border" />
                }
                ダウンロード
              </button>
            </div>
          </div>
        </div>
      </Card>
      <button className="btn btn-primary" type="button" onClick={() => setShowDownloadAllModal(true)} disabled={downloadingAll}>
        {
          downloadingAll
              && <Spinner size="sm" animation="border" />
        }
        全データExcelダウンロード
      </button>
    </main>
  );
}

export default ExcelDownload;
